// Iter 62ah — Calendly widget for founders (Overview)
// Shows the next 5 scheduled Calendly meetings from the connected PAT account.
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Calendar, ExternalLink, Video } from './icons';
import { PWAInstallButton } from './PWAInstallButton';

export function CalendlyWidget({ embedded = false }) {
  const [state, setState] = useState({ loading: true, connected: false, events: [] });
  // Iter 62ak — Budget filter pill (tous | gros | moyen | petit)
  const [budgetFilter, setBudgetFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/calendly/events?days_ahead=30');
        if (!cancelled) {
          setState({
            loading: false,
            connected: !!r.data?.connected,
            events: (r.data?.events || []),
          });
        }
      } catch (e) {
        if (!cancelled) setState({ loading: false, connected: false, events: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Iter 62ak — Filter + count events per tier
  const tierCounts = { all: state.events.length, gros: 0, moyen: 0, petit: 0, none: 0 };
  state.events.forEach((e) => {
    if (e.budget_tier === 'gros') tierCounts.gros += 1;
    else if (e.budget_tier === 'moyen') tierCounts.moyen += 1;
    else if (e.budget_tier === 'petit') tierCounts.petit += 1;
    else tierCounts.none += 1;
  });
  const filteredEvents = state.events
    .filter((e) => {
      if (budgetFilter === 'all') return true;
      if (budgetFilter === 'none') return !e.budget_tier;
      return e.budget_tier === budgetFilter;
    })
    .slice(0, 8);

  if (state.loading) {
    return <div className="shimmer" style={{ height: 84, borderRadius: 14, margin: embedded ? 0 : '0 22px 18px' }} />;
  }
  // Iter 62cj — friendly empty state when the user hasn't connected Calendly yet.
  // Shows a "Connecter Calendly" CTA that deep-links to /reglages.
  if (!state.connected) {
    return (
      <div
        data-testid="calendly-widget-empty"
        style={{
          margin: embedded ? 0 : '0 22px 18px',
          borderRadius: 16,
          padding: '22px 20px',
          background: 'linear-gradient(180deg, rgba(245,197,106,0.05), rgba(255,255,255,0.01)), var(--sf)',
          border: '1px solid var(--bd)',
          textAlign: 'center',
        }}
      >
        <div style={{
          width: 44, height: 44, borderRadius: 12, margin: '0 auto 12px',
          background: 'linear-gradient(140deg, #F5C56A 0%, rgba(255,255,255,0.15) 50%, #F5C56A 100%)',
          display: 'grid', placeItems: 'center',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), 0 4px 12px rgba(245,197,106,0.35)',
        }}>
          <Calendar size={20} color="#0B1220" strokeWidth={2.4} />
        </div>
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: 'var(--t1)', marginBottom: 4 }}>
          Connecte ton Calendly
        </div>
        <div style={{ fontSize: 12, color: 'var(--t3)', lineHeight: 1.5, marginBottom: 14, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
          En 1 clic, tes prochains rendez-vous s&apos;affichent ici. Aucun token à copier — auth Calendly officielle.
        </div>
        <a
          href="/reglages"
          data-testid="calendly-widget-connect-cta"
          style={{
            display: 'inline-block',
            padding: '10px 22px',
            borderRadius: 999,
            background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
            color: '#0B1330',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
            textDecoration: 'none',
            boxShadow: '0 6px 16px rgba(62,123,250,0.35)',
          }}
        >
          Se connecter avec Calendly
        </a>
      </div>
    );
  }

  return (
    <div
      data-testid="calendly-widget"
      style={{
        margin: embedded ? 0 : '0 22px 18px',
        borderRadius: 16,
        background:
          'linear-gradient(180deg, rgba(245,197,106,0.05), rgba(255,255,255,0.01)), var(--sf)',
        border: '1px solid var(--bd)',
        boxShadow:
          'inset 0 1px 0 rgba(255,255,255,0.06), 0 6px 20px rgba(0,0,0,0.20)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '14px 18px',
        display: 'flex', alignItems: 'center', gap: 10,
        borderBottom: '1px solid var(--bd)',
      }}>
        <span
          aria-hidden
          style={{
            width: 30, height: 30, borderRadius: 8,
            display: 'grid', placeItems: 'center',
            background:
              'linear-gradient(140deg, #F5C56A 0%, rgba(255,255,255,0.15) 50%, #F5C56A 100%)',
            color: '#0B1220',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5), 0 4px 10px rgba(245,197,106,0.4)',
          }}
        >
          <Calendar size={14} strokeWidth={2.2} />
        </span>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
            color: 'var(--t1)', letterSpacing: '-0.01em',
          }}>Prochains RDV Calendly</div>
          <div style={{
            fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5,
            color: 'var(--t3)', marginTop: 1,
            textTransform: 'uppercase', letterSpacing: '0.08em',
          }}>
            {state.events.length} rdv · 30 prochains jours
          </div>
        </div>
        <a
          href="https://calendly.com/event_types/user/me"
          target="_blank"
          rel="noreferrer noopener"
          data-testid="calendly-widget-open"
          style={{
            padding: '6px 10px',
            borderRadius: 999,
            background: 'rgba(245,197,106,0.14)',
            border: '1px solid rgba(245,197,106,0.35)',
            color: '#F5C56A',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
            textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}
        >
          <ExternalLink size={11} strokeWidth={2} /> Calendly
        </a>
      </div>

      {/* Iter 62am — Install PWA / Add to Home Screen button (iOS-friendly) */}
      <div style={{
        padding: '10px 14px',
        borderBottom: '1px solid var(--bd)',
        display: 'flex', justifyContent: 'center',
      }}>
        <PWAInstallButton label="Activer le widget iPhone" target="/widget/calendar" />
      </div>

      {/* Iter 62ak — Budget filter pills */}
      {state.events.length > 0 && (
        <div
          data-testid="calendly-budget-filter"
          style={{
            display: 'flex', gap: 6, padding: '10px 14px 4px',
            borderBottom: '1px solid var(--bd)',
            flexWrap: 'wrap',
          }}
        >
          {[
            { id: 'all',   label: 'Tous',       accent: '#8A9DB8', count: tierCounts.all },
            { id: 'gros',  label: '+800€',      accent: '#34D399', count: tierCounts.gros },
            { id: 'moyen', label: '300-800€',   accent: '#F5C56A', count: tierCounts.moyen },
            { id: 'petit', label: '<300€',      accent: '#FBBF24', count: tierCounts.petit },
            { id: 'none',  label: '?',          accent: '#565F84', count: tierCounts.none },
          ].filter((p) => p.id === 'all' || p.count > 0).map((p) => {
            const active = budgetFilter === p.id;
            return (
              <button
                key={p.id}
                data-testid={`calendly-budget-filter-${p.id}`}
                onClick={() => setBudgetFilter(p.id)}
                style={{
                  padding: '5px 10px', borderRadius: 999,
                  background: active ? `${p.accent}22` : 'var(--sf2)',
                  border: `1px solid ${active ? p.accent : 'var(--bd)'}`,
                  color: active ? p.accent : 'var(--t3)',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  transition: 'all 0.15s var(--ease-out)',
                }}
              >
                {p.label}
                <span style={{
                  padding: '0.5px 5px', borderRadius: 999,
                  background: active ? `${p.accent}30` : 'var(--sf)',
                  color: active ? p.accent : 'var(--t4)',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 9,
                }}>{p.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Events list */}
      {filteredEvents.length === 0 ? (
        <div style={{
          padding: '18px 20px', textAlign: 'center',
          fontFamily: 'Sora', fontWeight: 600, fontSize: 12,
          color: 'var(--t3)',
        }}>
          {state.events.length === 0
            ? 'Aucun RDV programmé dans les 30 prochains jours.'
            : `Aucun RDV avec ce budget (${state.events.length} au total).`}
        </div>
      ) : (
        <div style={{ padding: '6px 10px 10px' }}>
          {filteredEvents.map((e) => (
            <CalendlyRow key={e.uri} event={e} />
          ))}
        </div>
      )}
    </div>
  );
}

function CalendlyRow({ event }) {
  const start = event.start_time ? new Date(event.start_time) : null;
  const day = start
    ? start.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
    : '';
  const hour = start
    ? start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : '';
  const joinUrl = event.location?.join_url;
  const isMeet = event.location?.type === 'google_conference';
  // Iter 62ak — Budget badge (green big / gold mid / amber small)
  const tierMeta = event.budget_tier === 'gros'  ? { color: '#34D399', label: '+800€' }
                 : event.budget_tier === 'moyen' ? { color: '#F5C56A', label: '300-800€' }
                 : event.budget_tier === 'petit' ? { color: '#FBBF24', label: '<300€' }
                 : null;
  return (
    <div
      data-testid={`calendly-event-${event.uri?.split('/').pop()}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 12px',
        borderRadius: 10,
        transition: 'background 0.12s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sf2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        minWidth: 52, padding: '6px 8px',
        borderRadius: 8,
        background: 'var(--sf2)',
        border: '1px solid var(--bd)',
      }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
          color: '#F5C56A', textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>{day.split(' ')[0]}</div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 15,
          color: 'var(--t1)', lineHeight: 1.1,
        }}>{day.split(' ')[1]}</div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 600, fontSize: 9.5,
          color: 'var(--t3)', textTransform: 'uppercase',
        }}>{day.split(' ')[2] || ''}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
          color: 'var(--t1)', letterSpacing: '-0.01em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{event.name}</div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
          color: 'var(--t3)', marginTop: 2,
          display: 'flex', alignItems: 'center', gap: 6,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {hour}
          {/* Iter 62aj — Show invitee name (fallback to invitees count) */}
          {event.invitee_name ? (
            <>
              <span style={{ color: 'var(--t4)' }}>·</span>
              <span style={{ color: '#5BBEFC', fontWeight: 700 }}>{event.invitee_name}</span>
            </>
          ) : event.invitees_counter?.active > 0 && (
            <span style={{ color: 'var(--t4)' }}>· {event.invitees_counter.active} invité(s)</span>
          )}
          {/* Iter 62ak — Budget badge (green big / gold mid / amber small) */}
          {tierMeta && (
            <span
              data-testid={`calendly-row-budget-${event.uri?.split('/').pop()}`}
              style={{
                marginLeft: 'auto',
                padding: '1.5px 7px', borderRadius: 999,
                background: `${tierMeta.color}22`,
                border: `1px solid ${tierMeta.color}55`,
                color: tierMeta.color,
                fontFamily: 'Sora', fontWeight: 800, fontSize: 9,
                letterSpacing: '0.04em',
              }}
            >{tierMeta.label}</span>
          )}
        </div>
      </div>
      {joinUrl && (
        <a
          href={joinUrl}
          target="_blank"
          rel="noreferrer noopener"
          data-testid={`calendly-join-${event.uri?.split('/').pop()}`}
          style={{
            padding: '6px 10px', borderRadius: 8,
            background: isMeet ? 'rgba(52,211,153,0.14)' : 'rgba(91,190,252,0.14)',
            border: `1px solid ${isMeet ? 'rgba(52,211,153,0.35)' : 'rgba(91,190,252,0.35)'}`,
            color: isMeet ? '#34D399' : '#5BBEFC',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
            textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', gap: 5,
            flexShrink: 0,
          }}
        >
          <Video size={11} strokeWidth={2} /> Rejoindre
        </a>
      )}
    </div>
  );
}
